import { Box, Grid, Link, Stack, Typography } from "@mui/material";
import { Sparkles } from "lucide-react";
import { FC, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import logo from "assets/icons/logo.svg";
import backgroundPattern from "assets/images/background-pattern.png";
import loginImage from "assets/images/login-image.png";

interface AuthLayoutProps {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footerLink?: { label: string; to: string };
}

const AuthLayout: FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  footerLink,
}) => {
  return (
    <Grid
      container
      minHeight="100dvh"
      sx={{
        bgcolor: (t: any) => t.palette.brandBackground?.secondary ?? "#264B9F",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${backgroundPattern})`,
          backgroundPosition: "center center",
          backgroundRepeat: "repeat",
          opacity: 0.01,
          pointerEvents: "none",
        },
      }}
    >
      <Grid
        item
        lg={6}
        xs={12}
        order={{ xs: 2, lg: 1 }}
        sx={{
          bgcolor: (t: any) => t.palette.brandBackground?.primary ?? "#FAF9F6",
          borderTopRightRadius: 40,
          borderTopLeftRadius: { xs: 40, lg: 0 },
          borderBottomRightRadius: { xs: 0, lg: 40 },
        }}
      >
        <Stack
          height="100%"
          direction="column"
          justifyContent="center"
          alignItems="center"
          px={2}
        >
          <Stack width="100%" maxWidth={540} spacing={3}>
            <Box
              sx={{ display: { xs: "block", lg: "none" }, textAlign: "center" }}
            >
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{ height: 40, objectFit: "contain" }}
              />
            </Box>
            <Typography variant="headingLG" fontWeight={700} sx={{ mb: 4 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="textSM"
                sx={{
                  color: (t: any) =>
                    t.palette.brandText?.secondary ?? "#818181",
                  mb: 2,
                }}
              >
                {subtitle}
              </Typography>
            )}
            {children}
            {footerLink && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Link
                  component={RouterLink}
                  to={footerLink.to}
                  underline="hover"
                  sx={{
                    fontSize: 14,
                    color: (t: any) =>
                      t.palette.brandText?.secondary ?? "#818181",
                  }}
                >
                  {footerLink.label}
                </Link>
              </Box>
            )}
          </Stack>
        </Stack>
      </Grid>

      <Grid
        item
        lg={6}
        xs={12}
        order={{ xs: 1, lg: 2 }}
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: 200, lg: "auto" },
        }}
      >
        <Box
          component="img"
          src={loginImage}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: (t: any) => {
              const c = t.palette.brandBackground?.secondary ?? "#264B9F";
              const hex = c.replace("#", "");
              return `linear-gradient(160deg, #${hex}D9 0%, #${hex}F2 100%)`;
            },
          }}
        />
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            py: 5,
            px: 3,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{
              p: 3,
              py: 1,
              background: (t: any) =>
                t.palette.brandBackground?.secondary ?? "#264B9F",
              borderRadius: 8,
            }}
          >
            <Box
              component="span"
              sx={{
                color: (t: any) => t.palette.brandWarning?.primary ?? "#EACA00",
                display: "inline-flex",
              }}
            >
              <Sparkles size={20} />
            </Box>
            <Typography
              variant="labelMD"
              sx={{
                color: (t: any) => t.palette.brandText?.inverse ?? "#fff",
                opacity: 0.9,
              }}
            >
              Smart credit decisions for lenders and brokers.
            </Typography>
          </Stack>

          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            sx={{ maxWidth: 520 }}
          >
            <Typography
              variant="headingXL"
              sx={{
                color: (t: any) => t.palette.brandText?.inverse ?? "#fff",
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              Make{" "}
              <Typography
                variant="headingXL"
                component="span"
                sx={{
                  color: (t: any) =>
                    t.palette.brandWarning?.primary ?? "#EACA00",
                }}
              >
                confident{" "}
              </Typography>
              <br />
              credit decisions
            </Typography>
            <Typography
              sx={{
                color: (t: any) => t.palette.brandText?.inverse ?? "#fff",
                opacity: 0.85,
                fontSize: 16,
                lineHeight: 1.5,
              }}
            >
              Evaluate risk, streamline approvals, and grow your portfolio with
              data-driven credit insights.
            </Typography>
          </Stack>

          <Grid container sx={{ display: { xs: "none", lg: "flex" } }}>
            <Grid item xs={4}>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="headingMD"
                  sx={{
                    color: (t: any) => t.palette.brandWarning?.primary,
                    lineHeight: 1.2,
                  }}
                >
                  7,000+
                </Typography>
                <Typography
                  sx={{
                    color: (t: any) => t.palette.brandText?.inverse ?? "#fff",
                    opacity: 0.9,
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  Credit evaluations processed
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="headingMD"
                  sx={{
                    color: (t: any) => t.palette.brandWarning?.primary,
                    lineHeight: 1.2,
                  }}
                >
                  100M+
                </Typography>
                <Typography
                  sx={{
                    color: (t: any) => t.palette.brandText?.inverse ?? "#fff",
                    opacity: 0.9,
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  In funding
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="headingMD"
                  sx={{
                    color: (t: any) => t.palette.brandWarning?.primary,
                    lineHeight: 1.2,
                  }}
                >
                  99%
                </Typography>
                <Typography
                  sx={{
                    color: (t: any) => t.palette.brandText?.inverse ?? "#fff",
                    opacity: 0.9,
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  Client satisfaction rate
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default AuthLayout;
